import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon'; // Assuming SafeIcon is in a common folder

const { FiGift, FiSmile, FiCalendar, FiUsers, FiGlobe, FiParty, FiArrowRight, FiHeart } = FiIcons;

const Celebrate = () => {
  const celebrationFeatures = [
    {
      icon: FiGift,
      title: 'Personalized Gifts',
      description: 'Handpicked and customized gifts for every special occasion, delivered with care.'
    },
    {
      icon: FiSmile,
      title: 'Surprise Planning',
      description: 'Expert planning for surprise parties, anniversaries, and special moments.'
    },
    {
      icon: FiCalendar,
      title: 'Event Coordination',
      description: 'Seamless coordination for birthdays, festivals, and family gatherings.'
    },
    {
      icon: FiUsers,
      title: 'Family Reunions',
      description: 'Organizing memorable family reunions and get-togethers, even across distances.'
    }
  ];

  const services = [
    {
      icon: FiParty,
      title: 'Birthday Celebrations',
      description: 'Organizing unforgettable birthday parties, from themes to catering.',
      features: ['Theme planning', 'Catering services', 'Decorations & entertainment', 'Guest management']
    },
    {
      icon: FiHeart,
      title: 'Anniversary Surprises',
      description: 'Crafting romantic and memorable anniversary surprises for your loved ones.',
      features: ['Gift selection & delivery', 'Romantic dinners', 'Surprise trips', 'Custom messages']
    },
    {
      icon: FiGift,
      title: 'Festival Preparations',
      description: 'Complete assistance for traditional festival celebrations, decor, and rituals.',
      features: ['Traditional decor setup', 'Sweet & savory preparations', 'Puja arrangements', 'Family coordination']
    },
    {
      icon: FiGlobe,
      title: 'Special Occasions',
      description: 'Planning for graduations, promotions, and other significant life milestones.',
      features: ['Event venue booking', 'Custom cakes', 'Photography & videography', 'Guest accommodations']
    },
    {
      icon: FiCalendar,
      title: 'Virtual Gatherings',
      description: 'Facilitating virtual celebrations and family calls for those far away.',
      features: ['Video call setup', 'Virtual games & activities', 'E-invites & reminders', 'Tech support']
    },
    {
      icon: FiUsers,
      title: 'Personalized Messages',
      description: 'Creating heartfelt video messages and personalized greetings from family and friends.',
      features: ['Video message collection', 'Custom editing', 'Scheduled delivery', 'Multi-language support']
    }
  ];



  const testimonials = [
    {
      name: 'Priya Sharma',
      location: 'London, UK',
      text: 'They organized my parents\' anniversary from abroad, and it was perfect! The personalized cake and decor were amazing.',
      image: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&h=150&fit=crop&crop=face'
    },
    {
      name: 'Amit Singh',
      location: 'New York, USA',
      text: 'My sister\'s birthday surprise was a huge success, thanks to their meticulous planning. Highly recommend!',
      image: 'https://images.unsplash.com/photo-1507003211169-e69fe25477eb?w=150&h=150&fit=crop&crop=face'
    },
    {
      name: 'Sneha Reddy',
      location: 'Sydney, Australia',
      text: 'They helped us celebrate Diwali with traditional grandeur, even when we couldn\'t be there. Truly a blessing!',
      image: 'https://images.unsplash.com/photo-1520813792240-56ff469264a0?w=150&h=150&fit=crop&crop=face'
    }
  ];

  const [currentIndex, setCurrentIndex] = React.useState(0);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  React.useEffect(() => {
    const timer = setInterval(handleNext, 4000);
    return () => clearInterval(timer);
  }, [currentIndex]); // Added currentIndex to dependency array

  return (
    <>
      <style>{`
        .testimonial-carousel-section {
          background: #f9fafb;
          height: 650px;
          display: flex;
          justify-content: center;
          align-items: center;
          padding-top: 4rem;
          padding-bottom: 6rem;
        }
        .carousel-wrapper {
          position: relative;
          width: 90%;
          max-width: 950px;
          height: 350px;
          perspective: 1200px;
        }
        .testimonial-slide {
          position: absolute;
          top: 0;
          left: 50%;
          width: 60%;
          max-width: 550px;
          transform-origin: center center;
          transform-style: preserve-3d;
          opacity: 0;
          transition: all 0.8s ease-in-out;
          filter: blur(8px);
        }
        .testimonial-slide.active { opacity: 1; transform: translateX(-50%) scale(1) rotateY(0deg); filter: none; z-index: 3; }
        .testimonial-slide.left { opacity: 0.6; transform: translateX(-140%) scale(0.85) rotateY(25deg); z-index: 2; }
        .testimonial-slide.right { opacity: 0.6; transform: translateX(40%) scale(0.85) rotateY(-25deg); z-index: 2; }
        .testimonial-card {
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(10px);
          border-radius: 1rem;
          padding: 40px 30px;
          text-align: center;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
          position: relative;
          min-height: 250px;
        }
        .testimonial-card img {
          width: 80px; height: 80px; border-radius: 9999px; object-fit: cover;
          position: absolute; top: -40px; left: 50%; transform: translateX(-50%);
          border: 4px solid #fff; box-shadow: 0 4px 10px rgba(0,0,0,0.15);
        }
        .testimonial-author { margin-top: 50px; }
        .testimonial-text { font-size: 1rem; color: #4b5563; margin-top: 1rem; line-height: 1.6; font-style: italic; }
        .stars { margin-top: 1rem; color: #f59e0b; font-size: 1.125rem; }
        .carousel-buttons {
          position: absolute; bottom: -60px; left: 50%; transform: translateX(-50%);
          display: flex; gap: 1.5rem; z-index: 5;
        }
        .carousel-buttons button {
          background: #1f2937; color: #fff; border: none; border-radius: 9999px;
          width: 40px; height: 40px; cursor: pointer; font-size: 18px; transition: background-color 0.3s;
        }
        .carousel-buttons button:hover { background-color: #C89B3C; }
        @media (max-width: 768px) { .testimonial-slide { width: 85%; } }
      `}</style>
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#faf6eb] to-primary-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <div className="w-20 h-20 bg-gold-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon={FiGift} className="text-4xl text-[#C89B3C]" />
              </div>
              <div className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold bg-gold-50 text-gold-700 border border-gold-200 mb-6">
                <span className="w-2 h-2 rounded-full bg-[#C89B3C] animate-pulse"></span>
                Pay as you choose the service
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Celebration & Event Planning
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                Make every moment special, no matter the distance. We plan and execute joyful celebrations for your loved ones back home.
              </p>
              <Link
                to="/contact" // Assuming a contact page for inquiries
                className="bg-[#C89B3C] text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-[#ab812f] transition-colors inline-flex items-center space-x-2"
              >
                <span>Plan Your Celebration</span>
                <SafeIcon icon={FiArrowRight} className="text-lg" />
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Celebration Features */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Why Choose Us for Celebrations?
              </h2>
              <p className="text-xl text-gray-600">
                We bring your celebrations to life with creativity, care, and flawless execution.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
              {celebrationFeatures.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-gold-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <SafeIcon icon={feature.icon} className="text-2xl text-[#C89B3C]" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Our Celebration Services
              </h2>
              <p className="text-xl text-gray-600">
                From intimate gatherings to grand celebrations, we handle every detail.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-shadow"
                >
                  <div className="w-12 h-12 bg-gold-100 rounded-lg flex items-center justify-center mb-4">
                    <SafeIcon icon={service.icon} className="text-2xl text-[#C89B3C]" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center space-x-2 text-sm text-gray-700">
                        <SafeIcon icon={FiIcons.FiCheckCircle} className="text-[#0E8A6A] text-xs flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                How It Works
              </h2>
              <p className="text-xl text-gray-600">
                Simple steps to make your celebrations truly special and seamless
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { step: '1', title: 'Choose Occasion', description: 'Select from birthdays, anniversaries, festivals, or custom surprise events.' },
                { step: '2', title: 'Plan & Customize', description: 'Design the decor, surprise experience, photography, or catering with your dedicated planner.' },
                { step: '3', title: 'Execution Back Home', description: 'Our team delivers gifts, sets up decorations, and coordinates the event.' },
                { step: '4', title: 'Cherish the Memory', description: 'Receive high-quality event photos, videos, and live virtual links.' }
              ].map((step, index) => (
                <motion.div
                  key={step.step}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-gray-50 rounded-xl p-6 border border-gray-200 shadow-md text-center hover:shadow-lg transition-shadow"
                >
                  <div className="w-16 h-16 bg-[#C89B3C] text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                    {step.step}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600 text-sm">{step.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="bg-gray-50 flex justify-center items-center relative overflow-hidden" style={{ height: '650px', paddingTop: '4rem', paddingBottom: '6rem' }}>
          {/* Animated background elements */}
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 90, 0],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "linear"
            }}
            className="absolute top-10 right-10 w-64 h-64 bg-blue-100 rounded-full opacity-20 blur-3xl"
          />
          <motion.div
            animate={{
              scale: [1.2, 1, 1.2],
              rotate: [0, -90, 0],
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: "linear"
            }}
            className="absolute bottom-10 left-10 w-64 h-64 bg-purple-100 rounded-full opacity-20 blur-3xl"
          />

          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="text-center mb-12"
            >
              <motion.h2
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-3xl font-bold text-gray-900 mb-4"
              >
                Joyful Moments, Happy Families
              </motion.h2>
              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-xl text-gray-600 max-w-2xl mx-auto"
              >
                Hear from our clients about their memorable celebration experiences.
              </motion.p>
            </motion.div>

            {/* 3D Carousel Container */}
            <div className="relative mx-auto" style={{ width: '90%', maxWidth: '950px', height: '350px', perspective: '1200px' }}>
              {testimonials.map((testimonial, index) => {
                const total = testimonials.length;
                const leftIndex = (currentIndex - 1 + total) % total;
                const rightIndex = (currentIndex + 1) % total;

                let positionClass = '';
                let style = {
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  width: '60%',
                  maxWidth: '550px',
                  transformOrigin: 'center center',
                  transformStyle: 'preserve-3d',
                  opacity: 0,
                  transition: 'all 0.8s ease-in-out',
                  filter: 'blur(8px)'
                };

                if (index === currentIndex) {
                  positionClass = 'z-30';
                  style = { ...style, opacity: 1, transform: 'translateX(-50%) scale(1) rotateY(0deg)', filter: 'none' };
                } else if (index === leftIndex) {
                  positionClass = 'z-20';
                  style = { ...style, opacity: 0.6, transform: 'translateX(-140%) scale(0.85) rotateY(25deg)', filter: 'blur(2px)' };
                } else if (index === rightIndex) {
                  positionClass = 'z-20';
                  style = { ...style, opacity: 0.6, transform: 'translateX(40%) scale(0.85) rotateY(-25deg)', filter: 'blur(2px)' };
                }

                return (
                  <div key={index} className={positionClass} style={style}>
                    <div className="bg-[#edf7f4] rounded-2xl shadow-2xl p-8 relative" style={{ minHeight: '320px' }}>
                      <div className="absolute inset-4 border-2 border-emerald-600/20 rounded-lg">
                        <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 text-5xl font-bold text-[#0E8A6A]">❝</div>
                        <div className="absolute -bottom-5 left-1/2 transform -translate-x-1/2 text-5xl font-bold text-[#0E8A6A]">❞</div>
                      </div>

                      <div className="relative z-10 pt-6 px-4 h-full flex flex-col justify-center">
                        <p className="text-gray-900 text-base leading-snug mb-4 font-medium text-center mt-8">
                          {testimonial.text}
                        </p>

                        <div className="flex justify-center gap-1 mb-3 text-yellow-500">
                          ★★★★★
                        </div>

                        <div className="text-center">
                          <p className="text-sm text-gray-700">
                            <span className="font-bold">{testimonial.name}</span>
                            <br />
                            <span className="text-gray-500 text-xs">{testimonial.location}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Navigation Buttons */}
              <div className="absolute left-1/2 transform -translate-x-1/2 flex gap-6 z-50" style={{ bottom: '-60px' }}>
                <button
                  onClick={handlePrev}
                  className="bg-gray-800 text-white rounded-full flex items-center justify-center hover:bg-[#C89B3C] transition-colors duration-300"
                  style={{ width: '40px', height: '40px', border: 'none', cursor: 'pointer', fontSize: '18px' }}
                  aria-label="Previous testimonial"
                >
                  &#10094;
                </button>
                <button
                  onClick={handleNext}
                  className="bg-gray-800 text-white rounded-full flex items-center justify-center hover:bg-[#C89B3C] transition-colors duration-300"
                  style={{ width: '40px', height: '40px', border: 'none', cursor: 'pointer', fontSize: '18px' }}
                  aria-label="Next testimonial"
                >
                  &#10095;
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-16 bg-[#0F2D52]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center text-white"
            >
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon={FiSmile} className="text-3xl text-white" />
              </div>
              <h2 className="text-3xl font-bold mb-4">Ready to Celebrate?</h2>
              <p className="text-xl text-gold-100 mb-8 max-w-2xl mx-auto">
                Let us handle the details so you can enjoy the moments. Start planning your next celebration today!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="bg-[#C89B3C] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#ab812f] transition-colors"
                >
                  Get a Free Quote
                </Link>
                <Link
                  to="/login"
                  className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-[#0F2D52] transition-colors"
                >
                  Learn More
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Celebrate;